import type { ReactNode } from 'react';
import { T } from 'gt-react';
import { CommentsCard } from '@/components/common/comments/CommentsCard';
import {
  useCreateTaskComment,
  useTaskComments,
} from 'src/pages/authenticated/inbox/hooks/use-task-comments';

/**
 * Compact free-text notes on a task. Prefer these for anytime feedback that is
 * not tied to closing the task; close-time feedback still lives on Complete /
 * Fail / Cancel.
 *
 * The rendering and the composer live in the shared `CommentsCard`; this binds
 * them to the TASK's own comment procedures. A pricelist update case binds the
 * same card to its own, which is what keeps the pricelist role off the generic
 * task queue.
 */
export function TaskCommentsCard({
  taskId,
  agentName,
  currentUserId,
  userNameById,
  className,
  heading,
}: {
  taskId: string;
  agentName?: string;
  currentUserId?: string;
  userNameById?: Map<string, string>;
  className?: string;
  /** Null when the host already labels the thread and shows its count. */
  heading?: ReactNode | null;
}) {
  const { data, isLoading } = useTaskComments(taskId);
  const createComment = useCreateTaskComment(taskId);
  const resolvedHeading =
    heading === undefined ? (
      <T context="heading for notes on the task, distinct from an embedded record's notes">
        Task Notes
      </T>
    ) : (
      heading
    );

  return (
    <CommentsCard
      comments={data?.comments ?? []}
      isLoading={isLoading}
      isSubmitting={createComment.isPending}
      onSubmit={async (body) => {
        await createComment.mutateAsync(body);
      }}
      heading={resolvedHeading}
      agentName={agentName}
      currentUserId={currentUserId}
      userNameById={userNameById}
      className={className}
    />
  );
}
